import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Sheet,
  FolderCog,
  UserRound,
} from "lucide-react";
import { DIAGNOSTIC } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusPill = ({ ok, okLabel = "OK", koLabel = "ERREUR", testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 font-mono text-xs uppercase px-2.5 py-1 rounded-sm border ${
      ok
        ? "bg-emerald-500/15 text-emerald-600 border-emerald-500/30"
        : "bg-rose-500/15 text-rose-600 border-rose-500/30"
    }`}
  >
    {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
    {ok ? okLabel : koLabel}
  </span>
);

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between py-3 border-b border-border/60 last:border-b-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    {children}
  </div>
);

export default function AdminGoogleDiagnostic() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  const runTest = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await axios.get(`${API}/admin/google-diagnostic`);
      setData(res.data);
    } catch (e) {
      setFetchError(
        "Impossible de contacter le serveur HFACS. Vérifiez que le backend est démarré."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    runTest();
  }, [runTest]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={DIAGNOSTIC.page}>
      <header className="border-b border-border/60 backdrop-blur-md bg-card/40">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="diagnostic-back-home-link"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            HFACS
          </Link>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Administration → Diagnostic Google
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
        <h1 className="font-heading font-black uppercase tracking-tight text-3xl sm:text-4xl mb-2">
          Diagnostic Google
        </h1>
        <p className="text-sm text-muted-foreground mb-8 max-w-2xl">
          Vérifie l'accès serveur au compte de service Google, à la base métier Google Sheets
          et au dossier documentaire Google Drive. Aucun fichier existant n'est modifié, renommé
          ou supprimé par ce test.
        </p>

        <button
          onClick={runTest}
          disabled={loading}
          data-testid={DIAGNOSTIC.testButton}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-primary-foreground font-medium text-sm px-5 py-2.5 rounded-sm transition-colors mb-8"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Tester la connexion
        </button>

        {fetchError && (
          <div className="mb-6 px-4 py-3 rounded-sm border border-rose-500/30 bg-rose-500/10 text-rose-700 text-sm">
            {fetchError}
          </div>
        )}

        {data && (
          <div className="space-y-6">
            <section className="bg-card border border-border rounded-md p-5">
              <div className="flex items-center gap-2 mb-3 text-muted-foreground">
                <UserRound className="h-4 w-4" />
                <h2 className="font-heading uppercase font-semibold tracking-wide text-sm">Compte de service</h2>
              </div>
              <p
                data-testid={DIAGNOSTIC.serviceAccountEmail}
                className="font-mono text-sm text-foreground break-all"
              >
                {data.service_account_email || "—"}
              </p>
            </section>

            <section className="bg-card border border-border rounded-md p-5">
              <div className="flex items-center gap-2 mb-1 text-muted-foreground">
                <Sheet className="h-4 w-4" />
                <h2 className="font-heading uppercase font-semibold tracking-wide text-sm">Google Sheets</h2>
              </div>
              <Row label="Connexion">
                <StatusPill ok={data.sheets.connected} testId={DIAGNOSTIC.sheetsConnection} />
              </Row>
              <Row label="Fichier accessible">
                <StatusPill
                  ok={data.sheets.file_accessible}
                  okLabel="OUI"
                  koLabel="NON"
                  testId={DIAGNOSTIC.sheetsFileAccessible}
                />
              </Row>
              <Row label="Onglets détectés">
                <span data-testid={DIAGNOSTIC.sheetsTabsCount} className="font-mono text-sm text-foreground">
                  {data.sheets.tabs_count}
                </span>
              </Row>
              {data.sheets.tabs?.length > 0 && (
                <div className="pt-3 flex flex-wrap gap-2">
                  {data.sheets.tabs.map((t) => (
                    <span
                      key={t.sheet_id}
                      data-testid={DIAGNOSTIC.sheetsTabItem}
                      className="font-mono text-[11px] uppercase px-2 py-1 rounded-sm bg-secondary text-secondary-foreground border border-border"
                    >
                      {t.title}
                    </span>
                  ))}
                </div>
              )}
              {data.sheets.error && (
                <div
                  data-testid={DIAGNOSTIC.sheetsError}
                  className="mt-3 px-3 py-2.5 rounded-sm border border-rose-500/30 bg-rose-500/10 text-rose-700 text-sm"
                >
                  {data.sheets.error}
                </div>
              )}
            </section>

            <section className="bg-card border border-border rounded-md p-5">
              <div className="flex items-center gap-2 mb-1 text-muted-foreground">
                <FolderCog className="h-4 w-4" />
                <h2 className="font-heading uppercase font-semibold tracking-wide text-sm">Google Drive</h2>
              </div>
              <Row label="Connexion">
                <StatusPill ok={data.drive.connected} testId={DIAGNOSTIC.driveConnection} />
              </Row>
              <Row label="Dossier accessible">
                <StatusPill
                  ok={data.drive.folder_accessible}
                  okLabel="OUI"
                  koLabel="NON"
                  testId={DIAGNOSTIC.driveFolderAccessible}
                />
              </Row>
              <Row label="Création dossier">
                <StatusPill ok={data.drive.folder_creation} testId={DIAGNOSTIC.driveFolderCreation} />
              </Row>
              {(data.drive.error || data.drive.creation_error) && (
                <div
                  data-testid={DIAGNOSTIC.driveError}
                  className="mt-3 px-3 py-2.5 rounded-sm border border-rose-500/30 bg-rose-500/10 text-rose-700 text-sm"
                >
                  {data.drive.error || data.drive.creation_error}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
